/* global __MF_BUILD_COMMIT__ */
// VersionWatcher — a signed-in tab must not keep running last night's code. T506 (2026-10-06), v0.1.
//
// WHY. Build bf1e60af went live at 01:23 UTC with a rebuilt CRM. A tab opened before it kept
// the old screens, and Saul sent three emails from them at 02:30 with nothing saying the
// page was out of date.
//
// WHAT IT DOES. On every route change inside /dashboard, and whenever the tab becomes
// visible again, it reads /build-info.json (no cache) and compares the live commit with the
// one this bundle was built from (__MF_BUILD_COMMIT__, set by vite.config.ts). When they
// differ: an idle page reloads itself at the same address, at most once per commit per tab;
// a page with typed input or a save/send still in flight gets one bar at the top with a
// Reload button instead. The decision itself is src/lib/versionDecision.ts.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { decideVersionAction } from "@/lib/versionDecision";
import { pageIsDirty, pageIsBusy, clearPageDirty } from "@/lib/pageActivity";

const MINE: string = typeof __MF_BUILD_COMMIT__ === "string" ? __MF_BUILD_COMMIT__ : "";
const RELOADED_KEY = "mf-version-reloaded-for";
const MIN_GAP_MS = 15_000;

function readReloadedFor(): string | null {
  try { return sessionStorage.getItem(RELOADED_KEY); } catch { return null; }
}

export function VersionWatcher() {
  const location = useLocation();
  const [showBar, setShowBar] = useState(false);
  const lastCheck = useRef(0);


  const check = useCallback(async (force = false) => {
    if (!MINE) return;
    const now = Date.now();
    if (!force && now - lastCheck.current < MIN_GAP_MS) return;
    lastCheck.current = now;
    let live: string | null = null;
    try {
      const r = await fetch(`/build-info.json?t=${now}`, { cache: "no-store" });
      if (!r.ok) return;
      live = (await r.json())?.commit ?? null;
    } catch { return; }
    const action = decideVersionAction({ mine: MINE, live, dirty: pageIsDirty(), busy: pageIsBusy(), reloadedFor: readReloadedFor() });
    if (action === "none") { setShowBar(false); return; }
    if (action === "bar") { setShowBar(true); return; }
    try { sessionStorage.setItem(RELOADED_KEY, String(live)); } catch { /* without storage, the bar instead */ setShowBar(true); return; }
    console.log(`[mf] a newer build is live (${String(live).slice(0, 8)}); reloading this idle page once`);
    window.location.reload();
  }, []);

  // Route changes inside the dashboard. Leaving a page clears its typed input.
  useEffect(() => {
    if (!location.pathname.startsWith("/dashboard")) return;
    clearPageDirty();
    void check();
  }, [location.pathname, location.search, check]);

  // The tab coming back into view.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible" && window.location.pathname.startsWith("/dashboard")) void check(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [check]);

  if (!showBar) return null;
  return (
    <div role="status" data-foxchat-id="version-bar"
         className="fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-3 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-sm text-foreground backdrop-blur">
      <span>A newer version of MentionFox is ready.</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" onClick={() => window.location.reload()}
                  className="rounded-md border border-amber-500 bg-amber-500 px-3 py-1 text-xs font-medium text-black hover:bg-amber-400">
            Reload
          </button>
        </TooltipTrigger>
        <TooltipContent>Loads the latest version. Nothing you have saved is lost.</TooltipContent>
      </Tooltip>
    </div>
  );
}

export default VersionWatcher;
